import React, { useState, useEffect, useCallback } from 'react';
import { 
  Settings, 
  Shield, 
  AlertTriangle, 
  Check, 
  X, 
  ChevronRight,
  Search,
  Filter,
  History,
  Info,
  ToggleLeft,
  ToggleRight,
  Loader2
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import { useAppDialog } from '@/hooks/useAppDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function FeatureFlagsPage() {
  const { dangerConfirm, confirm } = useAppDialog();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState(null);
  const [auditHistory, setAuditHistory] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Modal state
  const [confirmModal, setConfirmModal] = useState({ open: false, flag: null, newValue: false });
  const [reason, setReason] = useState('');
  const [detailDrawer, setDetailDrawer] = useState({ open: false, flag: null });

  const fetchData = useCallback(async () => {
    try {
      const token = localStorage.getItem('access_token');
      const [flagsRes, historyRes] = await Promise.all([
        fetch(`${API_URL}/api/production/feature-flags/ui`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/production/feature-flags/audit-history?limit=20`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (flagsRes.ok) {
        const flagsData = await flagsRes.json();
        setData(flagsData.data);
      }

      if (historyRes.ok) {
        const historyData = await historyRes.json();
        setAuditHistory(historyData.data || []);
      }
    } catch (error) {
      console.error('Error fetching feature flags:', error);
      toast.error('Failed to load feature flags');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleToggleClick = (flag, newValue) => {
    setConfirmModal({ open: true, flag, newValue });
    setReason('');
  };

  const handleKillSwitch = async (flag) => {
    const newKs = !flag.kill_switch;
    const opts = newKs
      ? {
          title: `Activate kill switch for "${flag.label}"?`,
          description: 'This will immediately disable this feature for ALL users in this organisation. The change is logged in the audit trail.',
          confirmLabel: 'Activate kill switch',
          testId: 'flag-kill-switch-activate',
        }
      : {
          title: `Deactivate kill switch for "${flag.label}"?`,
          description: 'Feature will resume based on its current enabled state.',
          confirmLabel: 'Deactivate',
          variant: 'warning',
          testId: 'flag-kill-switch-deactivate',
        };
    const ok = newKs ? await dangerConfirm(opts) : await confirm(opts);
    if (!ok) return;

    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_URL}/api/production/feature-flags/${flag.key}/kill-switch`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled: newKs, reason: `Kill switch ${newKs ? 'activated' : 'deactivated'} via Admin UI` })
      });
      if (response.ok) {
        toast.success(`Kill switch ${newKs ? 'activated' : 'deactivated'} for ${flag.label}`);
        fetchData();
      } else {
        toast.error('Failed to toggle kill switch');
      }
    } catch (e) {
      toast.error('Failed to toggle kill switch');
    }
  };

  const handleConfirmToggle = async () => {
    const { flag, newValue } = confirmModal;
    setSaving(true);

    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_URL}/api/production/feature-flags/${flag.key}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          enabled: newValue,
          reason: reason || `${newValue ? 'Enabled' : 'Disabled'} via Admin UI`
        })
      });

      if (response.ok) {
        toast.success(`${flag.label} ${newValue ? 'enabled' : 'disabled'} successfully`);
        setConfirmModal({ open: false, flag: null, newValue: false });
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update feature flag');
      }
    } catch (error) {
      console.error('Error updating flag:', error);
      toast.error('Failed to update feature flag');
    } finally {
      setSaving(false);
    }
  };

  const filteredFlags = data?.by_category?.map(category => ({
    ...category,
    flags: category.flags.filter(flag => {
      const matchesSearch = flag.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           flag.description.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory = categoryFilter === 'all' || flag.category === categoryFilter;
      const matchesStatus = statusFilter === 'all' || 
                           (statusFilter === 'enabled' && flag.enabled) ||
                           (statusFilter === 'disabled' && !flag.enabled);
      return matchesSearch && matchesCategory && matchesStatus;
    })
  })).filter(category => category.flags.length > 0);

  const killSwitchCount = data?.by_category?.reduce((sum, cat) =>
    sum + cat.flags.filter(f => f.kill_switch).length, 0) || 0;

  const getRiskBadge = (riskLevel) => {
    switch (riskLevel) {
      case 'high':
        return <Badge variant="destructive" className="text-xs">High Impact</Badge>;
      case 'medium':
        return <Badge variant="warning" className="text-xs bg-yellow-500">Medium</Badge>;
      default:
        return <Badge variant="secondary" className="text-xs">Low</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6" data-testid="feature-flags-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Settings className="h-6 w-6" />
            Feature Flags
          </h1>
          <p className="text-muted-foreground mt-1">
            Control rollout of upgrade modules without changing the core platform.
          </p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Features</p>
                <p className="text-3xl font-bold">{data?.summary?.total || 0}</p>
              </div>
              <Settings className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Enabled</p>
                <p className="text-3xl font-bold text-green-600">{data?.summary?.enabled || 0}</p>
              </div>
              <ToggleRight className="h-8 w-8 text-green-600" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Disabled</p>
                <p className="text-3xl font-bold text-gray-500">{data?.summary?.disabled || 0}</p>
              </div>
              <ToggleLeft className="h-8 w-8 text-gray-500" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">High Impact</p>
                <p className="text-3xl font-bold text-red-600">{data?.summary?.high_impact || 0}</p>
              </div>
              <AlertTriangle className="h-8 w-8 text-red-600" />
            </div>
          </CardContent>
        </Card>

        <Card data-testid="kill-switch-summary-card">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Kill Switches Active</p>
                <p className={`text-3xl font-bold ${killSwitchCount > 0 ? 'text-red-600' : 'text-green-600'}`}>{killSwitchCount}</p>
              </div>
              <Shield className={`h-8 w-8 ${killSwitchCount > 0 ? 'text-red-600' : 'text-green-600'}`} />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search features..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="feature-search"
          />
        </div>
        
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="w-[180px]" data-testid="category-filter">
            <Filter className="h-4 w-4 mr-2" />
            <SelectValue placeholder="Category" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {Object.entries(data?.categories || {}).map(([key, cat]) => (
              <SelectItem key={key} value={key}>{cat.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[150px]" data-testid="status-filter">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="enabled">Enabled</SelectItem>
            <SelectItem value="disabled">Disabled</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Feature List by Category */}
      <div className="space-y-6">
        {filteredFlags?.map((category) => (
          <Card key={category.key}>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">{category.label}</CardTitle>
              <CardDescription>{category.description}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {category.flags.map((flag) => (
                  <div 
                    key={flag.key}
                    className="flex items-center justify-between p-4 border rounded-lg hover:bg-accent/50 transition-colors"
                    data-testid={`feature-flag-${flag.key}`}
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium">{flag.label}</span>
                        {getRiskBadge(flag.risk_level)}
                        {flag.kill_switch && (
                          <Badge variant="destructive" className="text-xs" data-testid={`kill-switch-badge-${flag.key}`}>
                            KILL SWITCH
                          </Badge>
                        )}
                        {flag.enabled && !flag.kill_switch ? (
                          <Badge variant="outline" className="text-green-600 border-green-600">
                            <Check className="h-3 w-3 mr-1" /> Enabled
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-gray-500">
                            <X className="h-3 w-3 mr-1" /> {flag.kill_switch ? 'Killed' : 'Disabled'}
                          </Badge>
                        )}
                        {flag.rollout_percentage < 100 && flag.rollout_percentage > 0 && (
                          <Badge variant="secondary" className="text-xs">{flag.rollout_percentage}% rollout</Badge>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground">{flag.description}</p>
                      {flag.dependencies?.length > 0 && (
                        <p className="text-xs text-muted-foreground mt-1">
                          Depends on: {flag.dependencies.join(', ')}
                        </p>
                      )}
                    </div>
                    
                    <div className="flex items-center gap-3">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setDetailDrawer({ open: true, flag })}
                        data-testid={`view-details-${flag.key}`}
                      >
                        <Info className="h-4 w-4 mr-1" />
                        Details
                      </Button>
                      <Button
                        variant={flag.kill_switch ? "destructive" : "outline"}
                        size="sm"
                        onClick={() => handleKillSwitch(flag)}
                        data-testid={`kill-switch-${flag.key}`}
                        title={flag.kill_switch ? "Deactivate kill switch" : "Activate kill switch"}
                      >
                        <AlertTriangle className="h-4 w-4" />
                      </Button>
                      <Switch
                        checked={flag.enabled && !flag.kill_switch}
                        disabled={flag.kill_switch}
                        onCheckedChange={(checked) => handleToggleClick(flag, checked)}
                        data-testid={`toggle-${flag.key}`}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Recent Activity */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <History className="h-5 w-5" />
            Recent Changes
          </CardTitle>
          <CardDescription>Latest feature flag modifications</CardDescription>
        </CardHeader>
        <CardContent>
          {auditHistory.length === 0 ? (
            <p className="text-muted-foreground text-center py-4">No recent changes</p>
          ) : (
            <div className="space-y-3">
              {auditHistory.slice(0, 10).map((entry, idx) => (
                <div key={entry.id || idx} className="flex items-center justify-between py-2 border-b last:border-0">
                  <div className="flex items-center gap-2">
                    {entry.after?.value ? (
                      <Badge variant="outline" className="text-green-600">Enabled</Badge>
                    ) : (
                      <Badge variant="outline" className="text-red-600">Disabled</Badge>
                    )}
                    <span className="font-medium">{entry.flag_name}</span>
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {entry.timestamp ? new Date(entry.timestamp).toLocaleString() : 'N/A'}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Confirmation Modal */}
      <Dialog open={confirmModal.open} onOpenChange={(open) => !saving && setConfirmModal({ ...confirmModal, open })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {confirmModal.flag?.risk_level === 'high' && (
                <AlertTriangle className="h-5 w-5 text-red-500" />
              )}
              {confirmModal.newValue ? 'Enable' : 'Disable'} Feature
            </DialogTitle>
            <DialogDescription>
              You are about to {confirmModal.newValue ? 'enable' : 'disable'}{' '}
              <strong>{confirmModal.flag?.label}</strong>
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {confirmModal.flag?.risk_level === 'high' && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-800 flex items-start gap-2">
                  <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  {confirmModal.flag?.warning || 'This feature affects financial or correction workflows. Confirm only if validation is complete.'}
                </p>
              </div>
            )}
            
            <div>
              <label className="text-sm font-medium">Reason (optional)</label>
              <Textarea
                placeholder="Enter reason for this change..."
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="mt-1"
              />
            </div>
            
            <div className="text-sm space-y-1">
              <p><strong>Current State:</strong> {confirmModal.flag?.enabled ? 'Enabled' : 'Disabled'}</p>
              <p><strong>New State:</strong> {confirmModal.newValue ? 'Enabled' : 'Disabled'}</p>
              {confirmModal.flag?.impacted_modules?.length > 0 && (
                <p><strong>Impacted Modules:</strong> {confirmModal.flag.impacted_modules.join(', ')}</p>
              )}
            </div>
          </div>
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setConfirmModal({ open: false, flag: null, newValue: false })}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleConfirmToggle}
              disabled={saving}
              variant={confirmModal.flag?.risk_level === 'high' ? 'destructive' : 'default'}
            >
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Confirm {confirmModal.newValue ? 'Enable' : 'Disable'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail Drawer */}
      <Dialog open={detailDrawer.open} onOpenChange={(open) => setDetailDrawer({ ...detailDrawer, open })}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5" />
              {detailDrawer.flag?.label}
            </DialogTitle>
            <DialogDescription>Feature flag details and configuration</DialogDescription>
          </DialogHeader>
          
          {detailDrawer.flag && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Technical Key</p>
                  <p className="font-mono">{detailDrawer.flag.key}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Category</p>
                  <p>{data?.categories?.[detailDrawer.flag.category]?.label || detailDrawer.flag.category}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Phase</p>
                  <p>Phase {detailDrawer.flag.phase}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Risk Level</p>
                  <p>{getRiskBadge(detailDrawer.flag.risk_level)}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Default</p>
                  <p>{detailDrawer.flag.default ? 'Enabled' : 'Disabled'}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Current Status</p>
                  <p>{detailDrawer.flag.enabled ? (
                    <span className="text-green-600">Enabled</span>
                  ) : (
                    <span className="text-gray-500">Disabled</span>
                  )}</p>
                </div>
              </div>
              
              <div>
                <p className="text-muted-foreground text-sm mb-1">Description</p>
                <p className="text-sm">{detailDrawer.flag.description}</p>
              </div>
              
              {detailDrawer.flag.impacted_modules?.length > 0 && (
                <div>
                  <p className="text-muted-foreground text-sm mb-1">Impacted Modules</p>
                  <div className="flex flex-wrap gap-1">
                    {detailDrawer.flag.impacted_modules.map((mod) => (
                      <Badge key={mod} variant="outline">{mod}</Badge>
                    ))}
                  </div>
                </div>
              )}
              
              {detailDrawer.flag.dependencies?.length > 0 && (
                <div>
                  <p className="text-muted-foreground text-sm mb-1">Dependencies</p>
                  <div className="flex flex-wrap gap-1">
                    {detailDrawer.flag.dependencies.map((dep) => (
                      <Badge key={dep} variant="secondary">{dep}</Badge>
                    ))}
                  </div>
                </div>
              )}
              
              <div>
                <p className="text-muted-foreground text-sm mb-1">Rollback Action</p>
                <p className="text-sm">{detailDrawer.flag.rollback_action}</p>
              </div>

              {/* Day 23: Enhanced controls */}
              <div className="pt-2 border-t">
                <p className="text-muted-foreground text-sm mb-2 font-medium">Production Controls</p>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-muted-foreground">Kill Switch</p>
                    <p className={detailDrawer.flag.kill_switch ? 'text-red-600 font-medium' : ''}>{detailDrawer.flag.kill_switch ? 'ACTIVE' : 'Off'}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Rollout %</p>
                    <p>{detailDrawer.flag.rollout_percentage ?? 100}%</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Environment</p>
                    <p>{detailDrawer.flag.environment || 'All'}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Scoped Roles</p>
                    <p>{detailDrawer.flag.scoped_roles?.length ? detailDrawer.flag.scoped_roles.join(', ') : 'All roles'}</p>
                  </div>
                  {detailDrawer.flag.scoped_states?.length > 0 && (
                    <div>
                      <p className="text-muted-foreground">Scoped States</p>
                      <p>{detailDrawer.flag.scoped_states.join(', ')}</p>
                    </div>
                  )}
                  {detailDrawer.flag.scoped_branches?.length > 0 && (
                    <div>
                      <p className="text-muted-foreground">Scoped Branches</p>
                      <p>{detailDrawer.flag.scoped_branches.join(', ')}</p>
                    </div>
                  )}
                </div>
              </div>
              
              {detailDrawer.flag.updated_at && (
                <div className="pt-2 border-t">
                  <p className="text-xs text-muted-foreground">
                    Last updated: {new Date(detailDrawer.flag.updated_at).toLocaleString()}
                  </p>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setDetailDrawer({ open: false, flag: null })}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
